import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
} from '../components/ui/dialog';
import { Package, Gift, ShoppingCart, CreditCard, Calendar, Loader2, CheckCircle, Plus, Minus, ArrowRight, ArrowLeft, User, Phone, Banknote, Mail, Copy, ZoomIn, X, Truck, Download, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get image URL (for templates, samples, etc.)
const getImageUrl = (urlOrObj) => {
  if (!urlOrObj) return '';
  const url = typeof urlOrObj === 'string' ? urlOrObj : (urlOrObj.thumbnail_url || urlOrObj.url);
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Helper to get full-size image URL (not thumbnail)
const getFullImageUrl = (urlOrObj) => {
  if (!urlOrObj) return '';
  const url = typeof urlOrObj === 'string' ? urlOrObj : (urlOrObj.url || urlOrObj.thumbnail_url);
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Check if product has variations with templates (shape, size, style, etc.)
const hasTemplateVariation = (product) => {
  if (!product?.variations) return false;
  return product.variations.some(v => {
    const name = (v.name || '').toLowerCase();
    // Check for common variation names that have templates
    return name.includes('shape') || name.includes('size') || name.includes('style');
  });
};

// Check if a variation should show template previews
const isTemplateVariation = (variationName) => {
  const name = (variationName || '').toLowerCase();
  return name.includes('shape') || name.includes('size') || name.includes('style');
};

const PreOrderPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1 = browse/cart, 2 = checkout form
  
  const [form, setForm] = useState({
    child_first_name: '',
    child_last_name: '',
    division: '',
    team_name: '',
    jersey_number: '',
    position: '',
    age: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_phone: '',
    parent_email: ''
  });
  
  const [delivery, setDelivery] = useState({
    option: '', // 'local', 'rural', 'mail'
    address: '',
    city: '',
    postal_code: '',
    address_type: '', // 'residential' or 'commercial'
    notes: ''
  });
  
  const deliveryOptions = [
    { id: 'local', name: 'Personal Local Delivery', description: 'In Spruce Grove/Stony Plain', fee: 5 },
    { id: 'rural', name: 'Personal Delivery', description: 'Within 20km of Stony Plain/Rural', fee: 8 },
    { id: 'mail', name: 'Direct from Lab Delivery', description: 'Letter Rate (Max 8x12/500g)', fee: 10 }
  ];
  
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash'); // 'cash', 'card', or 'etransfer'
  const [lightboxImage, setLightboxImage] = useState(null); // For viewing sample images larger

  useEffect(() => {
    fetchEventData();
  }, [slug]);

  const fetchEventData = async () => {
    try {
      const response = await axios.get(`${API}/preorder/${slug}/public`);
      setEventData(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Event not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const isTeamPhotoOnlyName = (name) => (name || '').toUpperCase().includes('TEAM PHOTO ONLY') || (name || '').toUpperCase().trim() === 'TEAM PHOTO';

  const addToCart = (type, item) => {
    const isTPO = isTeamPhotoOnlyName(item.name);
    const cartHasOther = cart.some(c => !isTeamPhotoOnlyName(c.name));
    const cartHasTPO = cart.some(c => isTeamPhotoOnlyName(c.name));
    if (isTPO && cartHasOther) {
      toast.info("No need to add this! Every child in the team photo automatically receives a FREE team photo with any order.", { duration: 7000 });
      return;
    }
    if (isTPO && cartHasTPO) {
      toast.info('"Team Photo Only" is already in your cart.', { duration: 5000 });
      return;
    }
    let baseCart = cart;
    if (!isTPO && cartHasTPO) {
      baseCart = cart.filter(c => !isTeamPhotoOnlyName(c.name));
      toast.info('"Team Photo Only" was removed from your cart — every child automatically receives the FREE team photo with any order.', { duration: 7000 });
    }
    if (type === 'product' && item.variations && item.variations.length > 0) {
      setCart([...baseCart, {
        type,
        id: item.id,
        name: item.name,
        basePrice: item.price,
        price: item.price,
        quantity: 1,
        variations: {},
        productData: item,
        customizations: {}
      }]);
      toast.success(`Added ${item.name} to cart - select options below`);
      return;
    }
    if (type === 'package' && (item.items || []).some(pi => pi.product?.variations?.length > 0)) {
      setCart([...baseCart, {
        type,
        id: item.id,
        name: item.name,
        basePrice: item.price,
        price: item.price,
        quantity: 1,
        variations: {},
        pkgVariations: {},
        packageData: item,
        customizations: {}
      }]);
      toast.success(`Added ${item.name} to cart`);
      return;
    }
    
    const existingIndex = baseCart.findIndex(
      c => c.type === type && c.id === item.id && 
      JSON.stringify(c.variations) === JSON.stringify({})
    );
    
    if (existingIndex >= 0) {
      const newCart = [...baseCart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...baseCart, {
        type,
        id: item.id,
        name: item.name,
        basePrice: item.price,
        price: item.price,
        quantity: 1,
        variations: {},
        customizations: {}
      }]);
    }
    toast.success(`Added ${item.name} to cart`);
  };

  const updateCartItemQuantity = (index, change) => {
    const newCart = [...cart];
    newCart[index].quantity += change;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const updateCartItemVariation = (index, variationName, optionName) => {
    const newCart = [...cart];
    newCart[index].variations[variationName] = optionName;
    const item = newCart[index];
    const baseItem = item.type === 'package' 
      ? eventData.packages.find(p => p.id === item.id)
      : eventData.products.find(p => p.id === item.id);
    
    let price = baseItem.price;
    if (item.type === 'product' && baseItem.variations) {
      for (const [varName, optName] of Object.entries(newCart[index].variations)) {
        const variation = baseItem.variations.find(v => v.name === varName);
        const option = variation?.options?.find(o => o.name === optName);
        if (option) price += option.price_adjustment || 0;
      }
    }
    newCart[index].price = price;
    setCart(newCart);
  };

  const setPkgOption = (index, pkgIdx, variationName, optionName, count) => {
    const newCart = [...cart];
    const ci = { ...newCart[index] };
    const pv = { ...(ci.pkgVariations || {}) };
    const entry = { ...(pv[pkgIdx] || {}) };
    if (count > 0) entry[variationName] = { option: optionName, count }; else delete entry[variationName];
    if (Object.keys(entry).length > 0) pv[pkgIdx] = entry; else delete pv[pkgIdx];
    ci.pkgVariations = pv;
    let price = ci.basePrice;
    for (const [idx, vars] of Object.entries(pv)) {
      const pi = ci.packageData?.items?.[idx];
      if (!pi) continue;
      for (const [vn, sel] of Object.entries(vars)) {
        const v = pi.product?.variations?.find(x => x.name === vn);
        const o = v?.options?.find(x => x.name === sel.option);
        if (o) price += (o.price_adjustment || 0) * (sel.count || 1);
      }
    }
    ci.price = price;
    newCart[index] = ci;
    setCart(newCart);
  };

  const pkgOptionSummary = (item) => {
    if (!item.pkgVariations || Object.keys(item.pkgVariations).length === 0) return '';
    return Object.entries(item.pkgVariations).map(([pkgIdx, vars]) => {
      const pi = item.packageData?.items?.[pkgIdx];
      const qty = pi?.quantity || 1;
      return Object.entries(vars).map(([k, sel]) => {
        const countLabel = qty > 1 ? ` (${sel.count || 1} of ${qty})` : '';
        return `+ ${k}${countLabel} — ${pi?.product?.name || ''}`;
      }).join(', ');
    }).join(', ');
  };

  const updateCartItemCustomText = (index, text) => {
    const newCart = [...cart];
    newCart[index].customizations = newCart[index].customizations || {};
    newCart[index].customizations.custom_text = text;
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 1)), 0);
  };

  const calculateTax = () => {
    const taxRate = eventData?.tax_rate || 0;
    return calculateSubtotal() * (taxRate / 100);
  };

  // Check if order is ONLY "Team Photo Only" (no bonuses)
  const isTeamPhotoOnlyOrder = () => {
    if (cart.length !== 1) return false;
    const itemName = (cart[0].name || '').toUpperCase().trim();
    return itemName === 'TEAM PHOTO ONLY';
  };

  // Check if cart contains only digital products (no physical delivery needed)
  // Check if order is digital-only, all items are free ($0), or team photo only
  const isDigitalOnlyOrFreeOrder = () => {
    if (cart.length === 0) return false;
    
    // Check if all items are free ($0)
    const allFree = cart.every(item => {
      if (item.type === 'product') {
        const product = eventData?.products?.find(p => p.id === item.id);
        return (product?.price || 0) === 0;
      } else if (item.type === 'package') {
        const pkg = eventData?.packages?.find(p => p.id === item.id);
        return (pkg?.price || 0) === 0;
      }
      return false;
    });
    
    if (allFree) return true;
    
    // Check if order is TEAM PHOTO ONLY (single item with that name)
    if (cart.length === 1) {
      const item = cart[0];
      if (item.type === 'package') {
        const pkg = eventData?.packages?.find(p => p.id === item.id);
        const pkgName = (pkg?.name || '').toUpperCase();
        if (pkgName.includes('TEAM PHOTO ONLY') || pkgName === 'TEAM PHOTO') {
          return true;
        }
      } else if (item.type === 'product') {
        const product = eventData?.products?.find(p => p.id === item.id);
        const prodName = (product?.name || '').toUpperCase();
        if (prodName.includes('TEAM PHOTO ONLY') || prodName === 'TEAM PHOTO') {
          return true;
        }
      }
    }
    
    // Check if all items are digital
    return cart.every(item => {
      if (item.type === 'product') {
        const product = eventData?.products?.find(p => p.id === item.id);
        return product?.category === 'digital';
      } else if (item.type === 'package') {
        const pkg = eventData?.packages?.find(p => p.id === item.id);
        if (!pkg || !pkg.items) return false;
        return pkg.items.every(pkgItem => {
          const product = eventData?.products?.find(p => p.id === pkgItem.product_id);
          return product?.category === 'digital';
        });
      }
      return false;
    });
  };
  
  // Keep old name for backwards compatibility
  const isDigitalOnlyOrder = isDigitalOnlyOrFreeOrder;

  // Event-level toggle: when off, clients never choose or pay for delivery
  const eventDeliveryDisabled = eventData?.event?.delivery_fees_enabled === false;

  const getDeliveryFee = () => {
    // No delivery fee for digital-only orders or events with delivery fees off
    if (isDigitalOnlyOrder() || eventDeliveryDisabled) return 0;
    const option = deliveryOptions.find(o => o.id === delivery.option);
    return option ? option.fee : 0;
  };

  const getCashDiscount = () => {
    return 0; // Cash discount disabled (June 2026)
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + getDeliveryFee() - getCashDiscount();
  };

  const getUpsellSuggestions = () => {
    if (!eventData) return [];
    
    const cartProductIds = cart.filter(c => c.type === 'product').map(c => c.id);
    const cartPackageIds = cart.filter(c => c.type === 'package').map(c => c.id);
    
    const availableProducts = (eventData.products || [])
      .filter(p => !cartProductIds.includes(p.id))
      .map(p => ({ ...p, type: 'product' }));
    
    const availablePackages = (eventData.packages || [])
      .filter(p => !cartPackageIds.includes(p.id))
      .map(p => ({ ...p, type: 'package' }));
    
    const allAvailable = [...availableProducts, ...availablePackages]
      .filter(p => !isTeamPhotoOnlyName(p.name))
      .sort((a, b) => a.price - b.price);
    
    return allAvailable.slice(0, 3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.child_first_name || !form.child_last_name) {
      toast.error('Please enter the child\'s first and last name');
      return;
    }
    
    if (!form.parent_first_name || !form.parent_last_name) {
      toast.error('Please enter the parent\'s first and last name');
      return;
    }
    
    if (!form.parent_email || !form.parent_phone || !form.team_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (cart.length === 0) {
      toast.error('Please add at least one item to your order');
      return;
    }
    
    // Skip delivery validation for digital-only orders or events with delivery fees off
    const digitalOnly = isDigitalOnlyOrder() || eventDeliveryDisabled;
    
    if (!digitalOnly) {
      // Validate delivery options for physical products
      if (!delivery.option) {
        toast.error('Please select a delivery option');
        return;
      }
      
      if (!delivery.address || !delivery.city || !delivery.postal_code) {
        toast.error('Please fill in your complete delivery address');
        return;
      }
      
      if (!delivery.address_type) {
        toast.error('Please indicate if the address is residential or commercial');
        return;
      }
      
      // Validate no PO Box for mail delivery
      if (delivery.option === 'mail' && delivery.address.toLowerCase().includes('po box')) {
        toast.error('PO Boxes are not accepted for Direct from Lab delivery. Please provide a physical mailing address.');
        return;
      }
    }

    setSubmitting(true);

    try {
      const items = cart.map(item => ({
        product_id: item.type === 'product' ? item.id : null,
        package_id: item.type === 'package' ? item.id : null,
        quantity: item.quantity,
        selected_variations: item.variations,
        customizations: item.customizations,
        package_item_variations: item.type === 'package' && item.pkgVariations && Object.keys(item.pkgVariations).length > 0 ? item.pkgVariations : null
      }));

      const selectedDeliveryOption = deliveryOptions.find(o => o.id === delivery.option);
      
      // For digital-only orders, send null/empty delivery info
      const deliveryInfo = digitalOnly ? null : {
        option: delivery.option,
        option_name: selectedDeliveryOption?.name,
        fee: selectedDeliveryOption?.fee || 0,
        address: delivery.address,
        city: delivery.city,
        postal_code: delivery.postal_code,
        address_type: delivery.address_type,
        notes: delivery.notes
      };
      
      // Combine first and last names
      const childName = `${form.child_first_name} ${form.child_last_name}`.trim();
      const parentName = `${form.parent_first_name} ${form.parent_last_name}`.trim();
      
      const response = await axios.post(`${API}/preorders`, {
        event_id: eventData.event.id,
        child_name: childName,
        child_first_name: form.child_first_name,
        child_last_name: form.child_last_name,
        division: form.division,
        team_name: form.team_name,
        jersey_number: form.jersey_number,
        position: form.position || null,
        age: form.age,
        parent_name: parentName,
        parent_first_name: form.parent_first_name,
        parent_last_name: form.parent_last_name,
        parent_phone: form.parent_phone,
        parent_email: form.parent_email,
        items,
        payment_method: paymentMethod,
        delivery: deliveryInfo
      });

      const preorderId = response.data.preorder.id;

      if (paymentMethod === 'etransfer') {
        // Redirect to e-transfer instructions page
        navigate(`/preorder/etransfer-pending/${preorderId}?child=${encodeURIComponent(childName)}&total=${calculateTotal()}&event=${encodeURIComponent(slug)}`);
      } else if (paymentMethod === 'cash') {
        // Redirect to cash payment confirmation page
        navigate(`/preorder/cash-pending/${preorderId}?child=${encodeURIComponent(childName)}&total=${calculateTotal()}&event=${encodeURIComponent(slug)}`);
      } else {
        // Proceed with Stripe checkout
        const checkoutResponse = await axios.post(`${API}/preorders/${preorderId}/checkout`, null, {
          params: { origin_url: window.location.origin }
        });
        window.location.href = checkoutResponse.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create pre-order');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { event, packages: rawPackages, products: allProducts } = eventData;

  // Separate "Team Photo Only" from other packages
  const teamPhotoOnly = (rawPackages || []).find(p => (p.name || '').toUpperCase().trim() === 'TEAM PHOTO ONLY');
  const packages = (rawPackages || []).filter(p => (p.name || '').toUpperCase().trim() !== 'TEAM PHOTO ONLY');
  // Hide the standalone team photo product - the free team photo is automatic (featured card covers photo-only families)
  const products = (allProducts || []).filter(p => !(p.name || '').toUpperCase().includes('TEAM PHOTO'));

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl text-stone-800">{event.name}</CardTitle>
            {event.description && (
              <CardDescription>{event.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              {event.event_date && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-amber-600" />
                  <span>Photo Date: {new Date(event.event_date).toLocaleDateString()}</span>
                </div>
              )}
              {event.preorder_deadline && (
                <div className="flex items-center gap-2 text-red-600">
                  <Calendar className="h-4 w-4" />
                  <span>Deadline: {new Date(event.preorder_deadline).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            
          </CardContent>
        </Card>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div 
            className={`flex items-center gap-2 ${checkoutStep === 1 ? 'text-amber-600 font-semibold' : 'text-stone-400 cursor-pointer hover:text-amber-500'}`}
            onClick={() => checkoutStep === 2 && setCheckoutStep(1)}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${checkoutStep === 1 ? 'bg-amber-500 text-white' : 'bg-stone-200 hover:bg-amber-200'}`}>1</div>
            <span className="hidden sm:inline">Select Products</span>
          </div>
          <div className="w-12 h-0.5 bg-stone-200"></div>
          <div className={`flex items-center gap-2 ${checkoutStep === 2 ? 'text-amber-600 font-semibold' : 'text-stone-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${checkoutStep === 2 ? 'bg-amber-500 text-white' : 'bg-stone-200'}`}>2</div>
            <span className="hidden sm:inline">Checkout</span>
          </div>
        </div>

        {checkoutStep === 1 ? (
          /* STEP 1: Browse Products & Build Cart */
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              {/* Team Photo Only - Featured at top */}
              {teamPhotoOnly && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-amber-600" />
                    Team Photo
                  </h2>
                  <Card className="border-2 border-amber-200 bg-amber-50/50">
                    <CardContent className="p-4">
                      <div className="mb-3 p-3 bg-green-50 border border-green-300 rounded-lg" data-testid="team-photo-free-notice">
                        <p className="text-sm font-semibold text-green-800">
                          Every child who appears in the team photo automatically receives a FREE team photo — you do NOT need to add it to your order.
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Only choose "Team Photo Only" if you are NOT ordering anything else.
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-stone-800">{teamPhotoOnly.name}</h3>
                          {teamPhotoOnly.description && (
                            <p className="text-sm text-stone-600 mt-1">{teamPhotoOnly.description}</p>
                          )}
                          {teamPhotoOnly.items?.length > 0 && (
                            <div className="mt-3 text-sm text-stone-500">
                              <p className="font-medium mb-2">Includes:</p>
                              <div className="space-y-2">
                                {teamPhotoOnly.items?.map((item, idx) => {
                                  const product = item.product;
                                  return (
                                    <div key={idx} className="flex items-center gap-3 p-2 bg-white rounded">
                                      <span>{product?.name || 'Product'} x{item.quantity}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-lg font-bold text-amber-600">
                            {teamPhotoOnly.price === 0 ? 'FREE' : `$${teamPhotoOnly.price?.toFixed(2)}`}
                          </p>
                          <Button
                            size="sm"
                            className="mt-2 bg-amber-500 hover:bg-amber-600"
                            onClick={() => addToCart('package', teamPhotoOnly)}
                            data-testid={`add-package-${teamPhotoOnly.id}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Packages */}
              {packages.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-amber-600" />
                    Packages
                  </h2>
                  <div className="space-y-4">
                    {packages.map(pkg => (
                      <Card key={pkg.id} className="border-stone-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-stone-800">{pkg.name}</h3>
                              {pkg.description && (
                                <p className="text-sm text-stone-600 mt-1">{pkg.description}</p>
                              )}
                              <div className="mt-3 text-sm text-stone-500">
                                <p className="font-medium mb-2">Includes:</p>
                                <div className="space-y-2">
                                  {pkg.items?.map((item, idx) => {
                                    const product = item.product;
                                    // Show sample image if available, otherwise show template only if has shape/size variation
                                    const sampleImage = product?.sample_images?.[0];
                                    const showTemplate = !sampleImage && hasTemplateVariation(product) && item.template;
                                    
                                    return (
                                      <div key={idx} className="flex items-center gap-3 p-2 bg-stone-50 rounded">
                                        {sampleImage && (
                                          <div 
                                            className="relative cursor-pointer group"
                                            onClick={() => setLightboxImage({ url: getFullImageUrl(sampleImage), name: product?.name })}
                                          >
                                            <img 
                                              src={getImageUrl(sampleImage)} 
                                              alt={`${product?.name} sample`}
                                              className="h-12 max-w-[80px] object-contain border rounded bg-white group-hover:opacity-80 transition-opacity"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                              <ZoomIn className="h-4 w-4 text-amber-600" />
                                            </div>
                                          </div>
                                        )}
                                        {showTemplate && (
                                          <div 
                                            className="relative cursor-pointer group"
                                            onClick={() => setLightboxImage({ url: getFullImageUrl(item.template), name: `${product?.name} template` })}
                                          >
                                            <img 
                                              src={getImageUrl(item.template)} 
                                              alt={`${product?.name} template`}
                                              className="h-12 max-w-[80px] object-contain border rounded bg-white group-hover:opacity-80 transition-opacity"
                                            />
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                              <ZoomIn className="h-4 w-4 text-amber-600" />
                                            </div>
                                          </div>
                                        )}
                                        <span>{product?.name || 'Product'} x{item.quantity}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-amber-600">{formatCurrency(pkg.price)}</p>
                              <Button size="sm" className="mt-2 bg-amber-500 hover:bg-amber-600" onClick={() => addToCart('package', pkg)}>
                                <Plus className="h-4 w-4 mr-1" />Add
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Individual Products */}
              {products.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-800 mb-4">Individual Products</h2>
                  <div className="space-y-4">
                    {products.map(product => {
                      // Show all sample images if available, otherwise show template only if has template variation
                      const sampleImages = product.sample_images || [];
                      const showTemplate = sampleImages.length === 0 && hasTemplateVariation(product) && product.template;
                      
                      return (
                        <Card key={product.id} className="border-stone-200">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start gap-4">
                              {/* Sample images - show all, clickable */}
                              {sampleImages.length > 0 && (
                                <div className="flex-shrink-0 flex gap-1">
                                  {sampleImages.map((sampleImage, idx) => (
                                    <div 
                                      key={idx}
                                      className="relative cursor-pointer group"
                                      onClick={() => setLightboxImage({ url: getFullImageUrl(sampleImage), name: product.name })}
                                    >
                                      <img 
                                        src={getImageUrl(sampleImage)} 
                                        alt={`${product.name} sample ${idx + 1}`}
                                        className="h-16 max-w-[80px] object-contain border rounded bg-white group-hover:opacity-80 transition-opacity"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        <ZoomIn className="h-4 w-4 text-amber-600" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {showTemplate && (
                                <div 
                                  className="flex-shrink-0 relative cursor-pointer group"
                                  onClick={() => setLightboxImage({ url: getFullImageUrl(product.template), name: `${product.name} template` })}
                                >
                                  <img 
                                    src={getImageUrl(product.template)} 
                                    alt={`${product.name} template`}
                                    className="h-16 max-w-[100px] object-contain border rounded bg-white group-hover:opacity-80 transition-opacity"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                    <ZoomIn className="h-5 w-5 text-amber-600" />
                                  </div>
                                </div>
                              )}
                              <div className="flex-1">
                                <h3 className="font-medium text-stone-800">{product.name}</h3>
                                {product.description && <p className="text-sm text-stone-500 mt-1">{product.description}</p>}
                              </div>
                              <p className="text-lg font-bold text-amber-600">{formatCurrency(product.price)}</p>
                            </div>
                            
                            {product.variations && product.variations.length > 0 && (
                              <div className="mt-3 p-3 bg-slate-50 rounded-lg space-y-2">
                                <p className="text-xs font-medium text-stone-600">Options:</p>
                                {product.variations.map(variation => (
                                  <div key={variation.name} className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm text-stone-600">{variation.name}:</span>
                                    {variation.options?.map(option => (
                                      <Badge key={option.name} variant="outline" className="text-xs cursor-default">
                                        {option.name}
                                        {option.price_adjustment !== 0 && (
                                          <span className="text-amber-600 ml-1">+{formatCurrency(option.price_adjustment)}</span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <Button variant="outline" className="w-full mt-3" onClick={() => addToCart('product', product)}>
                              <Plus className="h-4 w-4 mr-1" />Add to Order
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Cart Sidebar */}
            <div>
              <Card className="border-amber-200 sticky top-4">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-amber-600" />
                    Your Order
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cart.length === 0 ? (
                    <p className="text-sm text-stone-500 text-center py-4">Select packages or products to get started</p>
                  ) : (
                    <>
                      {cart.map((item, index) => (
                        <div key={index} className="border-b pb-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.name}</p>
                              {Object.entries(item.variations).length > 0 && (
                                <p className="text-xs text-stone-500">
                                  {Object.entries(item.variations).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                </p>
                              )}
                              {pkgOptionSummary(item) && (
                                <p className="text-xs text-amber-700" data-testid={`cart-pkg-options-${index}`}>{pkgOptionSummary(item)}</p>
                              )}
                            </div>
                            <p className="font-semibold text-sm">{formatCurrency(item.price * item.quantity)}</p>
                          </div>
                          
                          {item.productData?.variations && item.productData.variations.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {item.productData.variations.map(variation => {
                                const showTemplatePreview = isTemplateVariation(variation.name);
                                const selectedOptionName = item.variations[variation.name];
                                const selectedOption = variation.options?.find(o => o.name === selectedOptionName);
                                
                                return (
                                  <div key={variation.name}>
                                    <Label className="text-xs text-stone-600">{variation.name} {variation.required && '*'}</Label>
                                    <Select value={item.variations[variation.name] || ''} onValueChange={(val) => updateCartItemVariation(index, variation.name, val)}>
                                      <SelectTrigger className="h-8 text-xs mt-1">
                                        <SelectValue placeholder={`Select ${variation.name}`} />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {variation.options?.map(option => (
                                          <SelectItem key={option.name} value={option.name}>
                                            {option.name}
                                            {option.price_adjustment !== 0 && (
                                              <span className="text-amber-600 ml-1">({option.price_adjustment > 0 ? '+' : ''}{formatCurrency(option.price_adjustment)})</span>
                                            )}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    
                                    {/* Show template preview for selected shape/size/style option */}
                                    {showTemplatePreview && selectedOption?.template && (
                                      <div className="mt-2 p-2 bg-stone-50 rounded flex items-center gap-2">
                                        <img 
                                          src={getImageUrl(selectedOption.template)} 
                                          alt={`${selectedOption.name}`}
                                          className="h-12 max-w-[80px] object-contain border rounded bg-white"
                                        />
                                        <span className="text-xs text-stone-600">{selectedOption.name}</span>
                                      </div>
                                    )}
                                    
                                    {/* Show custom text input when "Custom" option is selected */}
                                    {selectedOptionName?.toLowerCase() === 'custom' && (
                                      <div className="mt-2">
                                        <Label className="text-xs text-stone-600">Custom Text *</Label>
                                        <Input 
                                          placeholder="Enter your custom text"
                                          value={item.customizations?.custom_text || ''}
                                          onChange={(e) => updateCartItemCustomText(index, e.target.value)}
                                          className="h-8 text-xs mt-1"
                                          maxLength={50}
                                        />
                                        <p className="text-xs text-stone-400 mt-1">Max 50 characters</p>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {item.type === 'package' && item.packageData?.items?.some(pi => pi.product?.variations?.length > 0) && (
                            <div className="mt-2 space-y-1.5 bg-amber-50/60 border border-amber-100 rounded-lg p-2">
                              <p className="text-xs font-medium text-stone-600">Optional add-ons:</p>
                              {item.packageData.items.map((pi, pkgIdx) =>
                                (pi.product?.variations || []).map(variation =>
                                  (variation.options || []).map(option => {
                                    const sel = item.pkgVariations?.[pkgIdx]?.[variation.name];
                                    const count = sel?.option === option.name ? (sel.count || 1) : 0;
                                    const maxQty = pi.quantity || 1;
                                    const testId = `pkg-option-${index}-${pkgIdx}-${variation.name.replace(/\s+/g, '-').toLowerCase()}`;
                                    if (maxQty > 1) {
                                      return (
                                        <div
                                          key={`${pkgIdx}-${variation.name}-${option.name}`}
                                          className="flex items-center justify-between gap-2 text-xs text-stone-700"
                                          data-testid={testId}
                                        >
                                          <span>
                                            {variation.name} for {pi.product?.name}
                                            <span className="text-amber-600 font-semibold ml-1">
                                              +{formatCurrency(option.price_adjustment || 0)} each
                                            </span>
                                          </span>
                                          <span className="flex items-center gap-1">
                                            <Button variant="outline" size="sm" className="h-5 w-5 p-0" disabled={count <= 0} data-testid={`${testId}-minus`} onClick={() => setPkgOption(index, pkgIdx, variation.name, option.name, Math.max(0, count - 1))}><Minus className="h-3 w-3" /></Button>
                                            <span className="w-9 text-center font-medium" data-testid={`${testId}-count`}>{count} of {maxQty}</span>
                                            <Button variant="outline" size="sm" className="h-5 w-5 p-0" disabled={count >= maxQty} data-testid={`${testId}-plus`} onClick={() => setPkgOption(index, pkgIdx, variation.name, option.name, Math.min(maxQty, count + 1))}><Plus className="h-3 w-3" /></Button>
                                          </span>
                                        </div>
                                      );
                                    }
                                    return (
                                      <label
                                        key={`${pkgIdx}-${variation.name}-${option.name}`}
                                        className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer"
                                        data-testid={testId}
                                      >
                                        <input
                                          type="checkbox"
                                          className="h-3.5 w-3.5 accent-amber-600"
                                          checked={count > 0}
                                          onChange={(e) => setPkgOption(index, pkgIdx, variation.name, option.name, e.target.checked ? 1 : 0)}
                                        />
                                        <span>
                                          {variation.name} for {pi.product?.name}
                                          {(option.price_adjustment || 0) !== 0 && (
                                            <span className="text-amber-600 font-semibold ml-1">
                                              +{formatCurrency(option.price_adjustment || 0)}
                                            </span>
                                          )}
                                        </span>
                                      </label>
                                    );
                                  })
                                )
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => updateCartItemQuantity(index, -1)}><Minus className="h-3 w-3" /></Button>
                              <span className="text-sm w-6 text-center">{item.quantity}</span>
                              <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => updateCartItemQuantity(index, 1)}><Plus className="h-3 w-3" /></Button>
                            </div>
                            <Button variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => removeFromCart(index)}>Remove</Button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Upsell */}
                      {getUpsellSuggestions().length > 0 && (
                        <div className="border-t pt-3">
                          <p className="text-xs font-medium text-stone-600 mb-2 flex items-center gap-1">
                            <span className="text-amber-500">★</span> Most parents also add...
                          </p>
                          <div className="space-y-2">
                            {getUpsellSuggestions().map(item => (
                              <div key={item.id} className="flex items-center justify-between bg-amber-50 rounded-lg p-2 border border-amber-100">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-stone-800 truncate">{item.name}</p>
                                  <p className="text-xs text-amber-600 font-semibold">Only {formatCurrency(item.price)}!</p>
                                </div>
                                <Button size="sm" variant="outline" className="ml-2 h-7 text-xs border-amber-300 hover:bg-amber-100" onClick={() => addToCart(item.type, item)}>
                                  <Plus className="h-3 w-3 mr-1" />Add
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Totals */}
                      <div className="border-t pt-3 space-y-1">
                        <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatCurrency(calculateSubtotal())}</span></div>
                        {eventData?.tax_rate > 0 && (
                          <div className="flex justify-between text-sm text-stone-500"><span>Tax ({eventData.tax_rate}%)</span><span>{formatCurrency(calculateTax())}</span></div>
                        )}
                        {getDeliveryFee() > 0 && (
                          <div className="flex justify-between text-sm text-stone-500">
                            <span>{deliveryOptions.find(o => o.id === delivery.option)?.name || 'Delivery'}</span>
                            <span>{formatCurrency(getDeliveryFee())}</span>
                          </div>
                        )}
                        {getCashDiscount() > 0 && (
                          <div className="flex justify-between text-sm text-green-600 font-medium">
                            <span>Cash Discount (10%)</span>
                            <span>-{formatCurrency(getCashDiscount())}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg"><span>Total</span><span className={paymentMethod === 'cash' ? 'text-green-600' : 'text-amber-600'}>{formatCurrency(calculateTotal())}</span></div>
                      </div>
                      
                      <Button className="w-full bg-amber-500 hover:bg-amber-600" onClick={() => {
                        // Validate cart items
                        for (const item of cart) {
                          if (item.type === 'product' && item.productData?.variations) {
                            for (const variation of item.productData.variations) {
                              // Check required variations are filled
                              if (variation.required && !item.variations[variation.name]) {
                                toast.error(`Please select ${variation.name} for ${item.name}`);
                                return;
                              }
                              // Check custom text is filled when "Custom" is selected
                              const selectedOption = item.variations[variation.name];
                              if (selectedOption?.toLowerCase() === 'custom' && !item.customizations?.custom_text?.trim()) {
                                toast.error(`Please enter custom text for ${item.name}`);
                                return;
                              }
                            }
                          }
                        }
                        setCheckoutStep(2);
                      }}>
                        Proceed to Checkout<ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* STEP 2: Checkout Form */
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-amber-600" />Order Summary</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setCheckoutStep(1)} className="text-stone-500"><ArrowLeft className="h-4 w-4 mr-1" />Edit Order</Button>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="space-y-2 mb-4">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm py-1 border-b border-dashed">
                      <span>
                        {item.name} x{item.quantity}
                        {Object.entries(item.variations).length > 0 && (
                          <span className="text-xs text-stone-500 ml-1">({Object.entries(item.variations).map(([k, v]) => `${k}: ${v}`).join(', ')})</span>
                        )}
                        {pkgOptionSummary(item) && (
                          <span className="text-xs text-amber-700 ml-1">({pkgOptionSummary(item)})</span>
                        )}
                      </span>
                      <span className="font-medium">{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-1 border-t pt-3">
                  <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatCurrency(calculateSubtotal())}</span></div>
                  {eventData?.tax_rate > 0 && (
                    <div className="flex justify-between text-sm text-stone-500"><span>Tax ({eventData.tax_rate}%)</span><span>{formatCurrency(calculateTax())}</span></div>
                  )}
                  {getDeliveryFee() > 0 && (
                    <div className="flex justify-between text-sm text-stone-500" data-testid="checkout-delivery-fee">
                      <span>{deliveryOptions.find(o => o.id === delivery.option)?.name || 'Delivery'}</span>
                      <span>{formatCurrency(getDeliveryFee())}</span>
                    </div>
                  )}
                  {getCashDiscount() > 0 && (
                    <div className="flex justify-between text-sm text-green-600 font-medium">
                      <span>Cash Discount (10%)</span>
                      <span>-{formatCurrency(getCashDiscount())}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>Total</span><span className={paymentMethod === 'cash' ? 'text-green-600' : 'text-amber-600'}>{formatCurrency(calculateTotal())}</span></div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="mt-6">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2"><User className="h-5 w-5 text-amber-600" />Player Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="child_first_name">Child's First Name *</Label>
                      <Input id="child_first_name" value={form.child_first_name} onChange={(e) => setForm({ ...form, child_first_name: e.target.value })} placeholder="First name" required />
                    </div>
                    <div>
                      <Label htmlFor="child_last_name">Child's Last Name *</Label>
                      <Input id="child_last_name" value={form.child_last_name} onChange={(e) => setForm({ ...form, child_last_name: e.target.value })} placeholder="Last name" required />
                    </div>
                  </div>
                  
                  {event.teams?.length > 0 ? (
                    <div>
                      <Label>Division *</Label>
                      <Select value={form.division} onValueChange={(v) => setForm({ ...form, division: v })}>
                        <SelectTrigger><SelectValue placeholder="Select division" /></SelectTrigger>
                        <SelectContent>
                          {event.teams.map(team => (<SelectItem key={team} value={team}>{team}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="division">Division</Label>
                      <Input id="division" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} placeholder="Enter division" />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="team_name">Team Name *</Label>
                    <Input id="team_name" value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} placeholder="Enter team name" required />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="jersey_number">Jersey Number *</Label>
                      <Input id="jersey_number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })} placeholder="#" required />
                    </div>
                    <div>
                      <Label htmlFor="age">Age *</Label>
                      <Input id="age" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="Age" required />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="position">Position <span className="text-stone-400">(optional)</span></Label>
                    <Input id="position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="e.g., Forward, Goalie" />
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium text-stone-700 mb-4 flex items-center gap-2"><Phone className="h-4 w-4 text-amber-600" />Parent/Guardian Contact</h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="parent_first_name">Parent's First Name *</Label>
                          <Input id="parent_first_name" value={form.parent_first_name} onChange={(e) => setForm({ ...form, parent_first_name: e.target.value })} placeholder="First name" required />
                        </div>
                        <div>
                          <Label htmlFor="parent_last_name">Parent's Last Name *</Label>
                          <Input id="parent_last_name" value={form.parent_last_name} onChange={(e) => setForm({ ...form, parent_last_name: e.target.value })} placeholder="Last name" required />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="parent_phone">Phone *</Label>
                        <Input id="parent_phone" type="tel" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} placeholder="(555) 123-4567" required />
                      </div>

                      <div>
                        <Label htmlFor="parent_email">Email *</Label>
                        <Input id="parent_email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} placeholder="your@email.com" required />
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-stone-500 italic">* Contact information is required for order-related communication only.</p>

                  {/* Delivery Options - Only show for physical products */}
                  {!isDigitalOnlyOrder() && !eventDeliveryDisabled ? (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium text-stone-700 mb-4 flex items-center gap-2">
                      <Truck className="h-4 w-4 text-amber-600" />
                      Delivery Option *
                    </h3>
                    
                    <div className="space-y-3 mb-4">
                      {deliveryOptions.map((option) => (
                        <div 
                          key={option.id}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            delivery.option === option.id 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                          onClick={() => setDelivery({ ...delivery, option: option.id })}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              delivery.option === option.id ? 'border-amber-500' : 'border-stone-300'
                            }`}>
                              {delivery.option === option.id && <div className="w-3 h-3 rounded-full bg-amber-500" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{option.name}</p>
                              <p className="text-sm text-stone-500">{option.description}</p>
                            </div>
                            <span className="font-semibold text-amber-600">${option.fee.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {delivery.option && (
                      <div className="space-y-4 p-4 bg-stone-50 rounded-lg">
                        <p className="text-sm font-medium text-stone-600">
                          {delivery.option === 'mail' 
                            ? '📬 Mailing Address (No PO Boxes)' 
                            : '📍 Physical Delivery Address'}
                        </p>
                        
                        <div>
                          <Label htmlFor="delivery_address">Street Address *</Label>
                          <Input 
                            id="delivery_address" 
                            value={delivery.address} 
                            onChange={(e) => setDelivery({ ...delivery, address: e.target.value })} 
                            placeholder={delivery.option === 'mail' ? 'Mailing address (no PO Boxes)' : 'Street address'} 
                            required 
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="delivery_city">City *</Label>
                            <Input 
                              id="delivery_city" 
                              value={delivery.city} 
                              onChange={(e) => setDelivery({ ...delivery, city: e.target.value })} 
                              placeholder="City" 
                              required 
                            />
                          </div>
                          <div>
                            <Label htmlFor="delivery_postal">Postal Code *</Label>
                            <Input 
                              id="delivery_postal" 
                              value={delivery.postal_code} 
                              onChange={(e) => setDelivery({ ...delivery, postal_code: e.target.value })} 
                              placeholder="T7X 1A1" 
                              required 
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label className="mb-2 block">Address Type *</Label>
                          <div className="flex gap-4">
                            <div 
                              className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
                                delivery.address_type === 'residential' 
                                  ? 'border-amber-500 bg-amber-50' 
                                  : 'border-stone-200 hover:border-stone-300'
                              }`}
                              onClick={() => setDelivery({ ...delivery, address_type: 'residential' })}
                            >
                              <span className="text-sm font-medium">🏠 Residential</span>
                            </div>
                            <div 
                              className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
                                delivery.address_type === 'commercial' 
                                  ? 'border-amber-500 bg-amber-50' 
                                  : 'border-stone-200 hover:border-stone-300'
                              }`}
                              onClick={() => setDelivery({ ...delivery, address_type: 'commercial' })}
                            >
                              <span className="text-sm font-medium">🏢 Commercial</span>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="delivery_notes">Delivery Notes (Optional)</Label>
                          <textarea
                            id="delivery_notes"
                            value={delivery.notes}
                            onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })}
                            placeholder="Special instructions, buzzer code, leave at door, etc."
                            className="w-full mt-1 px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                            rows={2}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  ) : (
                    <div className="border-t pt-4 mt-4">
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        {isDigitalOnlyOrder() ? (
                          <p className="text-sm text-blue-800 flex items-center gap-2">
                            <Download className="h-4 w-4" />
                            <strong>Digital Download Only</strong> - No delivery required. You'll receive download links via email after payment.
                          </p>
                        ) : (
                          <p className="text-sm text-blue-800 flex items-center gap-2" data-testid="no-delivery-fee-note">
                            <Truck className="h-4 w-4" />
                            <strong>No Delivery</strong> - Orders will be available for pick up. No shipping address needed.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Payment Method Selection */}
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium text-stone-700 mb-4 flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-amber-600" />
                      Payment Method
                    </h3>
                    
                    <div className="space-y-3">
                      <div 
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          paymentMethod === 'cash' 
                            ? 'border-green-500 bg-green-50' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        onClick={() => setPaymentMethod('cash')}
                        data-testid="payment-cash"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            paymentMethod === 'cash' ? 'border-green-500' : 'border-stone-300'
                          }`}>
                            {paymentMethod === 'cash' && <div className="w-3 h-3 rounded-full bg-green-500" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium flex items-center gap-2 text-green-700">
                              <Banknote className="h-4 w-4" />
                              Pay with CASH at Registration
                            </p>
                            <p className="text-sm text-stone-500">Pay in person on picture day - exact change appreciated</p>
                          </div>
                        </div>
                      </div>

                      <div 
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          paymentMethod === 'card' 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        onClick={() => setPaymentMethod('card')}
                        data-testid="payment-card"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            paymentMethod === 'card' ? 'border-amber-500' : 'border-stone-300'
                          }`}>
                            {paymentMethod === 'card' && <div className="w-3 h-3 rounded-full bg-amber-500" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium flex items-center gap-2">
                              <CreditCard className="h-4 w-4" />
                              Pay with Card
                            </p>
                            <p className="text-sm text-stone-500">Visa, Mastercard, American Express</p>
                          </div>
                        </div>
                      </div>
                      
                      <div 
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          paymentMethod === 'etransfer' 
                            ? 'border-amber-500 bg-amber-50' 
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        onClick={() => setPaymentMethod('etransfer')}
                        data-testid="payment-etransfer"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            paymentMethod === 'etransfer' ? 'border-amber-500' : 'border-stone-300'
                          }`}>
                            {paymentMethod === 'etransfer' && <div className="w-3 h-3 rounded-full bg-amber-500" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium flex items-center gap-2">
                              <Banknote className="h-4 w-4" />
                              Pay by Interac e-Transfer
                            </p>
                            <p className="text-sm text-stone-500">Send payment to liss_nick@hotmail.com</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className={`w-full h-12 text-lg ${paymentMethod === 'cash' ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-500 hover:bg-amber-600'}`} disabled={submitting}>
                    {submitting ? (
                      <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Processing...</>
                    ) : paymentMethod === 'card' ? (
                      <><CreditCard className="h-5 w-5 mr-2" />Pay {formatCurrency(calculateTotal())}</>
                    ) : paymentMethod === 'cash' ? (
                      <><Banknote className="h-5 w-5 mr-2" />Reserve Order - {formatCurrency(calculateTotal())}</>
                    ) : (
                      <><Banknote className="h-5 w-5 mr-2" />Place Order - {formatCurrency(calculateTotal())}</>
                    )}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full" 
                    onClick={() => setCheckoutStep(1)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />Back to Products
                  </Button>
                  
                  <p className="text-xs text-center text-stone-500">
                    {paymentMethod === 'card' ? 'Secure payment powered by Stripe' : 
                     paymentMethod === 'cash' ? 'Bring exact cash to picture day registration. Your order will be held for you.' :
                     'You will receive e-Transfer instructions on the next page'}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Lightbox Dialog for viewing sample images larger */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
          <div className="relative">
            <button 
              onClick={() => setLightboxImage(null)}
              className="absolute top-2 right-2 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            {lightboxImage && (
              <div className="flex flex-col items-center p-4">
                <img 
                  src={lightboxImage.url} 
                  alt={lightboxImage.name}
                  className="max-h-[80vh] max-w-full object-contain rounded"
                />
                <p className="text-white text-sm mt-3">{lightboxImage.name}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreOrderPage;
